import PropTypes from "prop-types";
import BaseNode from "./BaseNode";
import { RealisticSolarPanel } from "./realistic";

/**
 * SolarPanelNode – Solar panel visualization.
 *
 * Supports:
 * - Realistic 3D SCADA design with sunlight sheen sweep & photon generation flux
 * - Premium glassmorphism layout
 * - Classic industrial SVG layout
 */
const SolarPanelNode = (props) => {
  return (
    <BaseNode {...props}>
      {({ isRunning, isOffline, statusColor, speedScale, ratio, designType, color, opacity, liveValue, unit, width, height }) => {
        if (designType === "realistic") {
          return (
            <RealisticSolarPanel
              liveValue={liveValue}
              unit={unit || "kW"}
              isRunning={isRunning}
              isOffline={isOffline}
              statusColor={statusColor}
              speedScale={speedScale}
              ratio={ratio}
              opacity={opacity}
              width={width}
              height={height}
            />
          );
        }

        const isGenerating = isRunning;
        const dotColor =
          statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#94a3b8" : "#94a3b8";

        // Solar pulse — faster as output value nears max
        const pulseDuration = isGenerating ? `${Math.max(0.5, 2.0 / (speedScale || 1))}s` : "0s";
        const showOldDesign = designType === "new_design" || designType === "classic";

        return (
          <svg
            width="80"
            height="84"
            viewBox="0 0 80 84"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              opacity,
              filter: isOffline ? "grayscale(100%) opacity(0.6)" : "none",
              overflow: "visible",
              transition: "all 0.3s ease",
            }}
          >
            {!showOldDesign ? (
              // ─── NEW PREMIUM GLASSMORPHIC DESIGN ───
              <>
                <defs>
                  {/* Frame gradients */}
                  <linearGradient id="alumFrame" x1="0" y1="0" x2="80" y2="0">
                    <stop offset="0%" stopColor="#94a3b8" />
                    <stop offset="50%" stopColor="#e2e8f0" />
                    <stop offset="100%" stopColor="#475569" />
                  </linearGradient>
                  <linearGradient id="siliconCell" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#1e3a8a" />
                    <stop offset="50%" stopColor="#1e40af" />
                    <stop offset="100%" stopColor="#172554" />
                  </linearGradient>
                  <filter id="sunGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="2" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>

                {/* Support leg bracket at the back (sturdier design) */}
                <path d="M30,48 L22,66 L46,66 L42,48" stroke="#64748b" strokeWidth="2" strokeLinejoin="round" fill="#e2e8f0" />
                <line x1="36" y1="48" x2="34" y2="66" stroke="#475569" strokeWidth="1.5" />
                <rect x="20" y="65" width="28" height="2" fill="#334155" />

                {/* Tilted/Inclined Solar Panel Box */}
                {/* 3D depth border (bottom side) */}
                <polygon points="8,40 72,40 68,42 12,42" fill="#475569" />
                {/* Main tilted face */}
                <polygon points="12,12 68,12 72,40 8,40" fill="url(#alumFrame)" stroke="#334155" strokeWidth="1" />
                {/* Inner silicon face */}
                <polygon points="14,14 66,14 69,38 11,38" fill="url(#siliconCell)" />

                {/* Monocrystalline Cell divisions (Grid lines) */}
                {/* Horizontal wire lines */}
                <line x1="12.5" y1="20" x2="67.5" y2="20" stroke="#f8fafc" strokeWidth="0.75" opacity="0.6" />
                <line x1="11.8" y1="26" x2="68.2" y2="26" stroke="#f8fafc" strokeWidth="0.75" opacity="0.6" />
                <line x1="11.2" y1="32" x2="68.8" y2="32" stroke="#f8fafc" strokeWidth="0.75" opacity="0.6" />
                {/* Vertical wire lines */}
                <line x1="23" y1="14" x2="21" y2="38" stroke="#f8fafc" strokeWidth="0.5" opacity="0.6" />
                <line x1="32" y1="14" x2="31" y2="38" stroke="#f8fafc" strokeWidth="0.5" opacity="0.6" />
                <line x1="41" y1="14" x2="41" y2="38" stroke="#f8fafc" strokeWidth="0.5" opacity="0.6" />
                <line x1="50" y1="14" x2="51" y2="38" stroke="#f8fafc" strokeWidth="0.5" opacity="0.6" />
                <line x1="59" y1="14" x2="61" y2="38" stroke="#f8fafc" strokeWidth="0.5" opacity="0.6" />

                {/* Glossy anti-reflective glass glare sweep animation when generating */}
                {isGenerating && (
                  <>
                    <polygon points="10,14 25,14 15,38 0,38" fill="#ffffff" opacity="0.45">
                      <animate attributeName="points"
                               values="10,14 25,14 15,38 0,38; 55,14 70,14 73,38 58,38; 10,14 25,14 15,38 0,38"
                               dur="2.2s"
                               repeatCount="indefinite" />
                    </polygon>
                    {/* Silicon cell active harvest glow */}
                    <polygon points="14,14 66,14 69,38 11,38" fill="#fef08a" opacity="0.1">
                      <animate attributeName="opacity" values="0.05;0.25;0.05" dur="1.8s" repeatCount="indefinite" />
                    </polygon>
                  </>
                )}

                {/* Animated sun rays descending onto panel when active */}
                {isGenerating && (
                  <g filter="url(#sunGlow)">
                    {/* Sun source on top-left (enlarged) */}
                    <circle cx="12" cy="12" r="9" fill="#f59e0b" opacity="0.9" />
                    <circle cx="12" cy="12" r="14" fill="#fbbf24" opacity="0.4">
                      <animate attributeName="r" values="11;17;11" dur="1.5s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="12" cy="12" r="20" fill="#fbbf24" opacity="0.15">
                      <animate attributeName="r" values="16;24;16" dur="2s" repeatCount="indefinite" />
                    </circle>
                    {/* Rays flowing towards panel */}
                    <line x1="20" y1="18" x2="32" y2="28" stroke="#fbbf24" strokeWidth="3" strokeDasharray="6 6">
                      <animate attributeName="stroke-dashoffset" values="18;0" dur="0.8s" repeatCount="indefinite" />
                    </line>
                    <line x1="14" y1="22" x2="22" y2="38" stroke="#fbbf24" strokeWidth="3" strokeDasharray="6 6">
                      <animate attributeName="stroke-dashoffset" values="18;0" dur="1s" repeatCount="indefinite" />
                    </line>
                    <line x1="26" y1="14" x2="48" y2="24" stroke="#fbbf24" strokeWidth="2.5" strokeDasharray="6 6">
                      <animate attributeName="stroke-dashoffset" values="18;0" dur="1.2s" repeatCount="indefinite" />
                    </line>

                    {/* Energy flow pulse down the mount leg */}
                    <circle cx="34" cy="52" r="3.5" fill="#fbbf24">
                      <animate attributeName="cy" values="48;64;48" dur="0.8s" repeatCount="indefinite" />
                    </circle>
                  </g>
                )}

                {/* Out cable connection indicator */}
                <rect x="70" y="27" width="6" height="6" fill={color} rx="1" />

                {/* Premium status dot */}
                <circle cx="72" cy="8" r="5" fill={dotColor} stroke="#ffffff" strokeWidth="1.5" filter={isGenerating ? "url(#sunGlow)" : "none"}>
                  {isGenerating && (
                    <animate attributeName="opacity" values="1;0.5;1" dur="1.2s" repeatCount="indefinite" />
                  )}
                </circle>
              </>
            ) : (
              // ─── CLASSIC INDUSTRIAL SVG DESIGN ───
              <>
                {/* Panel frame */}
                <rect
                  x="10" y="10" width="60" height="40" rx="3"
                  fill={isGenerating ? `${color}15` : `${color}08`}
                  stroke={isGenerating ? color : "#64748b"}
                  strokeWidth="2"
                />

                {/* Solar cells grid */}
                {[0, 1, 2].map((row) =>
                  [0, 1, 2, 3].map((col) => (
                    <rect
                      key={`${row}-${col}`}
                      x={14 + col * 14} y={14 + row * 12}
                      width="12" height="10" rx="1"
                      fill={isGenerating ? `${color}30` : `${color}10`}
                      stroke={color} strokeWidth="0.5"
                    />
                  ))
                )}

                {/* Panel stand */}
                <line x1="40" y1="50" x2="40" y2="62" stroke={color} strokeWidth="2" />
                <line x1="30" y1="62" x2="50" y2="62" stroke={color} strokeWidth="2.5" strokeLinecap="round" />

                {/* Sun rays when generating */}
                {isGenerating && (
                  <g style={{ animation: `live-solar-pulse ${pulseDuration} ease-in-out infinite` }}>
                    <line x1="5" y1="5" x2="15" y2="12" stroke={color} strokeWidth="1" opacity="0.4" />
                    <line x1="40" y1="2" x2="40" y2="10" stroke={color} strokeWidth="1" opacity="0.4" />
                    <line x1="75" y1="5" x2="65" y2="12" stroke={color} strokeWidth="1" opacity="0.4" />
                    <circle cx="40" cy="4" r="3" fill={color} opacity="0.3" />
                  </g>
                )}

                {/* Energy output indicator */}
                <rect x="68" y="30" width="10" height="6" fill={color} rx="2" />

                {/* Status dot - top right */}
                <circle cx="72" cy="7" r="3" fill={dotColor} />
              </>
            )}
            {/* Status label at the bottom */}
            <text x="40" y="79" textAnchor="middle" fontSize="7.5" fontWeight="800" fill={dotColor} style={{ letterSpacing: 0.5 }}>
              {statusColor === "green" ? "GENERATING" : statusColor === "red" ? "IDLE" : "OFFLINE"}
            </text>
          </svg>
        );
      }}
    </BaseNode>
  );
};

SolarPanelNode.propTypes = { data: PropTypes.object, selected: PropTypes.bool, id: PropTypes.string };
export default SolarPanelNode;
